import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { roadmapApi, RoadmapFull, RoadmapAction, RoadmapHistoryItem } from '../services/roadmapApi';
import {
  Map,
  CheckCircle,
  Circle,
  Clock,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  DollarSign,
  Shield,
  Target,
  FileText,
  Mail,
  Copy,
  Ban,
  ArrowRight,
  TrendingUp,
  Calendar,
  User,
  History,
  Filter,
  BarChart3,
  Zap
} from 'lucide-react';

// Status configuration
const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: React.ElementType }> = {
  'pending': { label: 'Pending', color: 'text-gray-600', bgColor: 'bg-gray-100', icon: Circle },
  'in_progress': { label: 'In Progress', color: 'text-blue-600', bgColor: 'bg-blue-100', icon: Clock },
  'completed': { label: 'Completed', color: 'text-green-600', bgColor: 'bg-green-100', icon: CheckCircle },
  'blocked': { label: 'Blocked', color: 'text-red-600', bgColor: 'bg-red-100', icon: AlertTriangle },
  'skipped': { label: 'Skipped', color: 'text-gray-400', bgColor: 'bg-gray-50', icon: Ban }
};

// Difficulty colors
const DIFFICULTY_CONFIG: Record<string, { label: string; color: string }> = {
  'easy': { label: 'Easy', color: 'text-green-600' },
  'moderate': { label: 'Moderate', color: 'text-yellow-600' },
  'hard': { label: 'Hard', color: 'text-red-600' }
};

// Action type icons
const ACTION_TYPE_ICONS: Record<string, React.ElementType> = {
  'document': FileText,
  'evidence': FileText,
  'clarification': Mail,
  'verification': CheckCircle,
  'resolution': Target,
  'task': Zap
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

export default function StaffRoadmapPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  
  const [roadmap, setRoadmap] = useState<RoadmapFull | null>(null);
  const [history, setHistory] = useState<RoadmapHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // UI state
  const [expandedActions, setExpandedActions] = useState<Set<string>>(new Set());
  const [showHistory, setShowHistory] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedAction, setSelectedAction] = useState<RoadmapAction | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);

  // Fetch roadmap data
  const fetchRoadmap = async () => {
    if (!reportId) return;
    
    try {
      setError(null);
      const [roadmapData, historyData] = await Promise.all([
        roadmapApi.getFullRoadmap(reportId),  // Already handles null gracefully
        roadmapApi.getHistory(reportId).catch(() => [])
      ]);
      
      setRoadmap(roadmapData);
      setHistory(historyData);
    } catch (err: any) {
      console.error('Failed to fetch roadmap:', err);
      // Don't show error for "not found" - that's expected for new cases
      if (!err.message?.includes('No roadmap found')) {
        setError(err.message || 'Failed to load roadmap');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoadmap();
  }, [reportId]);

  // Generate roadmap
  const handleGenerate = async (forceRefresh: boolean = false) => {
    if (!reportId) return;
    
    setGenerating(true);
    setError(null);
    try {
      await roadmapApi.generateRoadmap(reportId, forceRefresh);
      await fetchRoadmap();
    } catch (err: any) {
      // Provide helpful error message
      if (err.message?.includes('No agent analyses')) {
        setError('AI analysis required first. Please run the case through the analysis pipeline before generating a roadmap.');
      } else {
        setError(err.message || 'Failed to generate roadmap');
      }
    } finally {
      setGenerating(false);
    }
  };

  // Update action status
  const handleStatusChange = async (actionId: string, newStatus: string) => {
    if (!reportId) return;
    
    try {
      await roadmapApi.updateActionStatus(reportId, actionId, newStatus);
      await fetchRoadmap();
    } catch (err: any) {
      console.error('Failed to update status:', err);
    }
  };

  // Toggle action expansion
  const toggleAction = (actionId: string) => {
    const newExpanded = new Set(expandedActions);
    if (newExpanded.has(actionId)) {
      newExpanded.delete(actionId);
    } else {
      newExpanded.add(actionId);
    }
    setExpandedActions(newExpanded);
  };

  // Filter actions
  const filteredActions = roadmap?.actions.filter(action => {
    if (statusFilter === 'all') return true;
    return action.status === statusFilter;
  }) || [];

  // Calculate totals
  const totalValueUnlock = roadmap?.actions.reduce((sum, a) => sum + (a.value_unlock || 0), 0) || 0;
  const totalRiskMitigated = roadmap?.actions.reduce((sum, a) => sum + (a.risk_mitigated || 0), 0) || 0;
  const completedValueUnlock = roadmap?.actions
    .filter(a => a.status === 'completed')
    .reduce((sum, a) => sum + (a.value_unlock || 0), 0) || 0;

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Map className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Case Strengthening Roadmap</h1>
              <p className="text-gray-500 text-sm">Report: {reportId}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {roadmap && (
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <History className="w-4 h-4" />
                v{roadmap.version}
              </button>
            )}
            <button
              onClick={() => handleGenerate(true)}
              disabled={generating}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
              {roadmap ? 'Regenerate' : 'Generate'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {!roadmap ? (
          // No roadmap - show generate prompt
          <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
            <Map className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No Roadmap Generated</h2>
            <p className="text-gray-500 mb-6">
              Generate a case strengthening roadmap to see actionable steps for improving this case.
            </p>
            <button
              onClick={() => handleGenerate(false)}
              disabled={generating}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {generating ? 'Generating...' : 'Generate Roadmap'}
            </button>
          </div>
        ) : (
          <>
            {/* Version History Sidebar */}
            {showHistory && history.length > 0 && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                <h3 className="font-semibold text-gray-700 mb-3">Version History</h3>
                <div className="space-y-2">
                  {history.map((h) => (
                    <div
                      key={h.id}
                      className={`p-3 rounded-lg border ${h.is_current ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Version {h.version}</span>
                        <span className="text-sm text-gray-500">{formatDate(h.generated_at)}</span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {h.completed_actions}/{h.total_actions} complete ({h.percent_complete.toFixed(0)}%)
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Case Strength */}
              <div className="bg-white rounded-xl shadow-sm border p-4">
                <div className="flex items-center gap-2 text-gray-500 mb-2">
                  <Target className="w-4 h-4" />
                  <span className="text-sm font-medium">Case Strength</span>
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-bold text-gray-900">
                    {roadmap.current_state.case_strength}
                  </span>
                  <ArrowRight className="w-4 h-4 text-gray-400 mb-1" />
                  <span className="text-xl font-semibold text-green-600">
                    {roadmap.target_state.achievable_strength}
                  </span>
                </div>
                <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${roadmap.current_state.case_strength}%` }}
                  />
                </div>
              </div>

              {/* Progress */}
              <div className="bg-white rounded-xl shadow-sm border p-4">
                <div className="flex items-center gap-2 text-gray-500 mb-2">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Progress</span>
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-bold text-gray-900">
                    {roadmap.progress.completed_actions}
                  </span>
                  <span className="text-gray-500 mb-1">/ {roadmap.progress.total_actions} actions</span>
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {roadmap.progress.percent_complete.toFixed(0)}% complete
                </div>
              </div>

              {/* Value Unlock - STAFF ONLY */}
              <div className="bg-white rounded-xl shadow-sm border p-4">
                <div className="flex items-center gap-2 text-gray-500 mb-2">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm font-medium">Value Unlock</span>
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">Staff Only</span>
                </div>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(totalValueUnlock)}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {formatCurrency(completedValueUnlock)} unlocked so far
                </div>
              </div>

              {/* Risk Mitigated - STAFF ONLY */}
              <div className="bg-white rounded-xl shadow-sm border p-4">
                <div className="flex items-center gap-2 text-gray-500 mb-2">
                  <Shield className="w-4 h-4" />
                  <span className="text-sm font-medium">Risk Mitigated</span>
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">Staff Only</span>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {formatCurrency(totalRiskMitigated)}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {roadmap.current_state.vulnerabilities_count} vulnerabilities to address
                </div>
              </div>
            </div>

            {/* Financial Impact Summary - STAFF ONLY */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-100 p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Financial Impact Summary</h2>
                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">STAFF ONLY - Do not share with clients</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <div className="text-sm text-gray-500">Current Est. Value</div>
                  <div className="text-xl font-bold text-gray-900">
                    {formatCurrency(roadmap.current_state.estimated_value)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Potential Increase</div>
                  <div className="text-xl font-bold text-green-600">
                    +{formatCurrency(roadmap.target_state.estimated_value_increase)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">New Est. Value</div>
                  <div className="text-xl font-bold text-blue-600">
                    {formatCurrency(roadmap.target_state.new_estimated_value)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Unclaimed Damages</div>
                  <div className="text-xl font-bold text-amber-600">
                    {formatCurrency(roadmap.current_state.unclaimed_damages)}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions Filter */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Action Items</h2>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border rounded-lg px-3 py-1.5 text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="blocked">Blocked</option>
                </select>
              </div>
            </div>

            {/* Actions List */}
            <div className="space-y-3">
              {filteredActions.map((action) => {
                const isExpanded = expandedActions.has(action.id);
                const StatusIcon = STATUS_CONFIG[action.status]?.icon || Circle;
                const ActionIcon = ACTION_TYPE_ICONS[action.action_type] || FileText;
                const statusConfig = STATUS_CONFIG[action.status] || STATUS_CONFIG['pending'];
                const difficultyConfig = DIFFICULTY_CONFIG[action.difficulty] || DIFFICULTY_CONFIG['moderate'];
                
                return (
                  <div
                    key={action.id}
                    className="bg-white rounded-xl shadow-sm border overflow-hidden"
                  >
                    {/* Action Header */}
                    <div
                      className="p-4 cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleAction(action.id)}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${statusConfig.bgColor}`}>
                            <StatusIcon className={`w-4 h-4 ${statusConfig.color}`} />
                          </div>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-500">#{action.priority}</span>
                            <ActionIcon className="w-4 h-4 text-gray-400" />
                            <span className={`text-xs px-2 py-0.5 rounded ${statusConfig.bgColor} ${statusConfig.color}`}>
                              {statusConfig.label}
                            </span>
                            {action.is_estimate && (
                              <span className="text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-700">
                                Estimated
                              </span>
                            )}
                          </div>
                          
                          <h3 className="font-medium text-gray-900">{action.action}</h3>
                          
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {action.deadline_days} days
                            </span>
                            <span className={`flex items-center gap-1 ${difficultyConfig.color}`}>
                              {difficultyConfig.label}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              ~{action.estimated_minutes} min
                            </span>
                          </div>
                        </div>
                        
                        {/* Financial Impact - STAFF ONLY */}
                        <div className="flex-shrink-0 text-right">
                          {action.value_unlock > 0 && (
                            <div className="text-sm font-medium text-green-600">
                              +{formatCurrency(action.value_unlock)}
                            </div>
                          )}
                          {action.risk_mitigated > 0 && (
                            <div className="text-sm text-blue-600">
                              -{formatCurrency(action.risk_mitigated)} risk
                            </div>
                          )}
                          <div className="text-xs text-gray-400 mt-1">
                            +{action.impact_strength_points} pts
                          </div>
                        </div>
                        
                        <div className="flex-shrink-0">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t bg-gray-50 p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Left Column */}
                          <div className="space-y-4">
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-1">Impact Description</h4>
                              <p className="text-sm text-gray-600">{action.impact_description}</p>
                            </div>
                            
                            {action.addresses.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-1">Addresses</h4>
                                <ul className="text-sm text-gray-600 list-disc list-inside">
                                  {action.addresses.map((addr, i) => (
                                    <li key={i}>{addr}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {action.source_agents.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-1">Source Agents</h4>
                                <div className="flex gap-2">
                                  {action.source_agents.map((agent, i) => (
                                    <span key={i} className="text-xs px-2 py-1 bg-gray-200 rounded">
                                      {agent}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Right Column */}
                          <div className="space-y-4">
                            <div className="bg-white rounded-lg border p-3">
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Calculation Details</h4>
                              <div className="text-sm text-gray-600 space-y-1">
                                <div className="flex justify-between">
                                  <span>Method:</span>
                                  <span className="font-mono text-xs">{action.calculation_method || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>ID:</span>
                                  <span className="font-mono text-xs">{action.calculation_id || 'N/A'}</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Dependencies */}
                            {(action.depends_on.length > 0 || action.unlocks.length > 0) && (
                              <div className="bg-white rounded-lg border p-3">
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Dependencies</h4>
                                {action.depends_on.length > 0 && (
                                  <div className="text-sm text-gray-600 mb-2">
                                    <span className="text-red-600">Blocked by:</span> {action.depends_on.length} action(s)
                                  </div>
                                )}
                                {action.unlocks.length > 0 && (
                                  <div className="text-sm text-gray-600">
                                    <span className="text-green-600">Unlocks:</span> {action.unlocks.length} action(s)
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Status Actions */}
                            <div className="flex gap-2">
                              {action.status === 'pending' && (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStatusChange(action.id, 'in_progress');
                                    }}
                                    className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                  >
                                    Start
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStatusChange(action.id, 'completed');
                                    }}
                                    className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200"
                                  >
                                    Complete
                                  </button>
                                </>
                              )}
                              {action.status === 'in_progress' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange(action.id, 'completed');
                                  }}
                                  className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200"
                                >
                                  Mark Complete
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedAction(action);
                                  setShowEmailModal(true);
                                }}
                                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center gap-1"
                              >
                                <Mail className="w-3 h-3" />
                                Email
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {filteredActions.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                No actions match the current filter.
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
